// src/pages/admin/DisputeDetailPage.tsx
// ============================================================
// Summit League v2 — Dispute Detail Page
//
// Full dispute view with match context, both teams' reported
// results, and the resolution form. Auto-marks the
// admin_dispute_opened notification as read on mount.
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import DisputeStatusBadge from '../../components/admin/DisputeStatusBadge';
import DisputeResolutionForm from '../../components/admin/DisputeResolutionForm';
import Button from '../../components/ui/Button';
import type { Notification } from '../../types/notification.types';

import '../../styles/admin-disputes.css';

type DisputeStatus = 'open' | 'under_review' | 'resolved_team_a' | 'resolved_team_b' | 'resolved_draw' | 'dismissed';

interface TeamSummary {
  teamId: number;
  teamName: string;
  teamTag: string | null;
  logoUrl: string | null;
  rank: number | null;
  points: number;
}

interface MatchGame {
  matchGameId: number;
  gameNumber: number;
  gameType: string;
  mapName: string;
  teamAReportedWinner: number | null;
  teamBReportedWinner: number | null;
  winnerTeamId: number | null;
  status: string;
}

interface DisputeDetail {
  disputeId: number;
  matchId: number;
  matchGameId: number | null;
  match: {
    matchId: number;
    gameId: number;
    gameName: string;
    gameCode: string;
    seasonId: number;
    seasonName: string;
    teamA: TeamSummary;
    teamB: TeamSummary | null;
    matchType: string;
    matchState: string;
    matchFormat: string;
    teamAScore: number;
    teamBScore: number;
    games?: MatchGame[];
  };
  disputingTeam: TeamSummary | null;
  disputingUserId: number | null;
  isAutoCreated: boolean;
  reason: string;
  evidenceNote: string | null;
  status: DisputeStatus;
  resolvedByUserId: number | null;
  resolutionNotes: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface OutletContext {
  notifications: Notification[];
  markAsRead: (id: number) => Promise<void>;
}

export default function DisputeDetailPage() {
  const { disputeId } = useParams<{ disputeId: string }>();
  const navigate = useNavigate();
  const outletContext = useOutletContext<OutletContext>();

  const [dispute, setDispute] = useState<DisputeDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDispute = useCallback(async () => {
    if (!disputeId) return;
    setIsLoading(true);
    setError(null);

    try {
      const res = await axiosClient.get(`/disputes/${disputeId}`);
      setDispute(res.data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to load dispute';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [disputeId]);

  useEffect(() => {
    fetchDispute();
  }, [fetchDispute]);

  // Auto-mark admin_dispute_opened notification as read for this dispute
  useEffect(() => {
    if (!dispute || !outletContext?.notifications || !outletContext?.markAsRead) return;

    const matchingNotification = outletContext.notifications.find(
      (n) =>
        (n.type as string) === 'admin_dispute_opened' &&
        !n.isRead &&
        n.relatedId === dispute.disputeId
    );

    if (matchingNotification) {
      outletContext.markAsRead(matchingNotification.notificationId);
    }
  }, [dispute, outletContext]);

  const handleSetUnderReview = async () => {
    if (!disputeId) return;
    setIsSubmitting(true);
    try {
      const res = await axiosClient.post(`/disputes/${disputeId}/review`);
      setDispute(res.data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to update';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResolve = async (
    status: DisputeStatus,
    resolutionNotes: string,
    correctedWinnerId?: number,
    gameResults?: Record<number, number>
  ) => {
    if (!disputeId) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const res = await axiosClient.post(`/disputes/${disputeId}/resolve`, {
        status,
        resolutionNotes,
        correctedWinnerId,
        gameResults,
      });
      setDispute(res.data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Resolution failed';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTeamName = (teamId: number | null): string => {
    if (!dispute || !teamId) return 'Unknown';
    if (dispute.match.teamA.teamId === teamId) return dispute.match.teamA.teamName;
    if (dispute.match.teamB?.teamId === teamId) return dispute.match.teamB.teamName;
    return `Team #${teamId}`;
  };

  if (isLoading) {
    return (
      <div className="sl-admin-disputes">
        <div className="sl-admin-loading-inline">Loading dispute...</div>
      </div>
    );
  }

  if (error && !dispute) {
    return (
      <div className="sl-admin-disputes">
        <div className="sl-admin-error">{error}</div>
        <Button variant="ghost" onClick={() => navigate('/admin/disputes')}>
          Back to Disputes
        </Button>
      </div>
    );
  }

  if (!dispute) return null;

  const isOpen = dispute.status === 'open';
  const isUnderReview = dispute.status === 'under_review';
  const isResolvable = isOpen || isUnderReview;
  const isResolved = !isResolvable;

  return (
    <div className="sl-admin-disputes">
      {/* Header */}
      <div className="sl-admin-page-header">
        <div className="sl-dispute-detail-header">
          <button
            type="button"
            className="sl-dispute-back-btn"
            onClick={() => navigate('/admin/disputes')}
          >
            ← Back
          </button>
          <div>
            <h1 className="sl-admin-page-title">Dispute #{dispute.disputeId}</h1>
            <p className="sl-admin-page-subtitle">
              Match #{dispute.matchId} — {dispute.match.gameName} · {dispute.match.seasonName}
            </p>
          </div>
          <DisputeStatusBadge status={dispute.status} />
        </div>
      </div>

      {error && <div className="sl-admin-error sl-admin-error--inline">{error}</div>}

      <div className="sl-dispute-detail-grid">
        {/* Match Overview Card */}
        <div className="sl-card sl-dispute-card">
          <div className="sl-card-header">
            <h2 className="sl-admin-card-title">Match Overview</h2>
            <span className="sl-dispute-format-tag">
              {dispute.match.matchFormat.toUpperCase()} · {dispute.match.matchType.replace('_', ' ')}
            </span>
          </div>

          <div className="sl-dispute-teams-row">
            <div className="sl-dispute-team">
              <span className="sl-dispute-team-label">Team A</span>
              <span className="sl-dispute-team-name">{dispute.match.teamA.teamName}</span>
              {dispute.match.teamA.teamTag && (
                <span className="sl-dispute-team-tag">[{dispute.match.teamA.teamTag}]</span>
              )}
              <span className="sl-dispute-team-rank">
                Rank #{dispute.match.teamA.rank || '—'} · {dispute.match.teamA.points} pts
              </span>
            </div>

            <div className="sl-dispute-vs">
              <span className="sl-dispute-score">
                {dispute.match.teamAScore} - {dispute.match.teamBScore}
              </span>
              <span className="sl-dispute-vs-label">vs</span>
            </div>

            {dispute.match.teamB && (
              <div className="sl-dispute-team">
                <span className="sl-dispute-team-label">Team B</span>
                <span className="sl-dispute-team-name">{dispute.match.teamB.teamName}</span>
                {dispute.match.teamB.teamTag && (
                  <span className="sl-dispute-team-tag">[{dispute.match.teamB.teamTag}]</span>
                )}
                <span className="sl-dispute-team-rank">
                  Rank #{dispute.match.teamB.rank || '—'} · {dispute.match.teamB.points} pts
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Game Results Card */}
        {dispute.match.games && dispute.match.games.length > 0 && (
          <div className="sl-card sl-dispute-card">
            <div className="sl-card-header">
              <h2 className="sl-admin-card-title">Game Results</h2>
            </div>

            <div className="sl-dispute-games">
              {dispute.match.games.map((game) => {
                const isDisputedGame = dispute.matchGameId === game.matchGameId;
                return (
                  <div
                    key={game.matchGameId}
                    className={`sl-dispute-game-row ${isDisputedGame ? 'sl-dispute-game-row--disputed' : ''}`}
                  >
                    <div className="sl-dispute-game-info">
                      <span className="sl-dispute-game-number">Game {game.gameNumber}</span>
                      <span className="sl-dispute-game-type">
                        {game.mapName} — {game.gameType}
                      </span>
                      {isDisputedGame && (
                        <span className="sl-dispute-game-flag">Disputed</span>
                      )}
                    </div>
                    <div className="sl-dispute-game-reports">
                      <div className="sl-dispute-report">
                        <span className="sl-dispute-report-label">
                          {dispute.match.teamA.teamName} says:
                        </span>
                        <span className="sl-dispute-report-value">
                          {game.teamAReportedWinner
                            ? getTeamName(game.teamAReportedWinner)
                            : 'Not reported'}
                        </span>
                      </div>
                      <div className="sl-dispute-report">
                        <span className="sl-dispute-report-label">
                          {dispute.match.teamB?.teamName || 'Team B'} says:
                        </span>
                        <span className="sl-dispute-report-value">
                          {game.teamBReportedWinner
                            ? getTeamName(game.teamBReportedWinner)
                            : 'Not reported'}
                        </span>
                      </div>
                      {game.winnerTeamId && (
                        <div className="sl-dispute-report sl-dispute-report--confirmed">
                          <span className="sl-dispute-report-label">Confirmed:</span>
                          <span className="sl-dispute-report-value">
                            {getTeamName(game.winnerTeamId)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Dispute Details Card */}
        <div className="sl-card sl-dispute-card">
          <div className="sl-card-header">
            <h2 className="sl-admin-card-title">Dispute Details</h2>
          </div>

          <div className="sl-dispute-meta">
            <div className="sl-dispute-meta-row">
              <span className="sl-dispute-meta-label">Type</span>
              <span className="sl-dispute-meta-value">
                {dispute.isAutoCreated ? 'Auto-created (conflicting reports)' : 'Manual'}
              </span>
            </div>
            <div className="sl-dispute-meta-row">
              <span className="sl-dispute-meta-label">Reason</span>
              <span className="sl-dispute-meta-value">{dispute.reason || '—'}</span>
            </div>
            {dispute.evidenceNote && (
              <div className="sl-dispute-meta-row">
                <span className="sl-dispute-meta-label">Evidence</span>
                <span className="sl-dispute-meta-value">{dispute.evidenceNote}</span>
              </div>
            )}
            <div className="sl-dispute-meta-row">
              <span className="sl-dispute-meta-label">Created</span>
              <span className="sl-dispute-meta-value">
                {new Date(dispute.createdAt).toLocaleString()}
              </span>
            </div>
            {dispute.resolvedAt && (
              <div className="sl-dispute-meta-row">
                <span className="sl-dispute-meta-label">Resolved</span>
                <span className="sl-dispute-meta-value">
                  {new Date(dispute.resolvedAt).toLocaleString()}
                </span>
              </div>
            )}
            {dispute.resolutionNotes && (
              <div className="sl-dispute-meta-row">
                <span className="sl-dispute-meta-label">Resolution Notes</span>
                <span className="sl-dispute-meta-value">{dispute.resolutionNotes}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {isOpen && (
          <div className="sl-dispute-actions-bar">
            <Button
              variant="ghost"
              onClick={handleSetUnderReview}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Set Under Review'}
            </Button>
          </div>
        )}

        {/* Resolution Form */}
        {isResolvable && (
          <div className="sl-card sl-dispute-card">
            <DisputeResolutionForm
              currentStatus={dispute.status}
              teamA={dispute.match.teamA}
              teamB={dispute.match.teamB}
              games={dispute.match.games || []}
              matchFormat={dispute.match.matchFormat}
              isSubmitting={isSubmitting}
              onResolve={handleResolve}
            />
          </div>
        )}

        {/* Resolved indicator */}
        {isResolved && (
          <div className="sl-dispute-resolved-banner">
            This dispute has been resolved.
          </div>
        )}
      </div>
    </div>
  );
}